package components.project.configuration.layout.moduleLayout

import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.geometry.Coordinates
import it.neckar.geometry.Rectangle
import it.neckar.geometry.RightTriangleType
import it.neckar.geometry.Size
import it.neckar.lizergy.model.configuration.moduleLayout.planning.UnusableAreaInformation
import it.neckar.lizergy.model.configuration.moduleLayout.planning.UnusableAreasInformation
import it.neckar.open.kotlin.lang.log10
import it.neckar.open.kotlin.lang.nullIfBlank
import it.neckar.open.kotlin.lang.toIntCeil
import it.neckar.open.unit.si.mm
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import react.*
import react.dom.*

val EditUnusableArea: FC<EditUnusableAreaProps> = fc("EditUnusableArea") { props ->
  val unusableAreasInformationToSave = props::unusableAreasInformationToSave.getNotNull()
  val unusableArea = props::unusableArea.safeGet()
  val index = props::index.safeGet()
  val roofSize = props::roofSize.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val usableRoofArea = Rectangle(location = Coordinates.origin, size = roofSize)
  val lengthFactorForEditing = 10.0
  val numberOfDecimals = log10(lengthFactorForEditing.toInt())
  val lengthUnit = "cm"

  val descriptionToSave = useState(unusableArea.description)
  val xToSave = useState(unusableArea.location.x / lengthFactorForEditing)
  val yToSave = useState(unusableArea.location.y / lengthFactorForEditing)
  val widthToSave = useState(unusableArea.size.width / lengthFactorForEditing)
  val heightToSave = useState(unusableArea.size.height / lengthFactorForEditing)


  useMemo(unusableArea) {
    xToSave.setter(unusableArea.location.x / lengthFactorForEditing)
    yToSave.setter(unusableArea.location.y / lengthFactorForEditing)
    widthToSave.setter(unusableArea.size.width / lengthFactorForEditing)
    heightToSave.setter(unusableArea.size.height / lengthFactorForEditing)
  }

  useEffect(
    descriptionToSave.value,
    xToSave.value,
    yToSave.value,
    widthToSave.value,
    heightToSave.value,
  ) {
    unusableAreasInformationToSave.setter {
      it.copy(
        unusableAreas = it.unusableAreas.mapIndexed { i, area ->
          if (i == index) {
            area.copy(
              description = descriptionToSave.value.nullIfBlank(),
              location = Coordinates(xToSave.value * lengthFactorForEditing, yToSave.value * lengthFactorForEditing),
              size = Size(widthToSave.value * lengthFactorForEditing, heightToSave.value * lengthFactorForEditing),
            )
          } else {
            area
          }
        }
      )
    }
  }


  div("row g-0 my-3") {

    div("col-lg-6") {
      nullableFloatingInputField(
        valueAndSetter = descriptionToSave,
        fieldName = "unusableAreaDescription$index",
        title = "${index + 1}. Nicht-belegbare Fläche",
        editableStatus = editableStatus,
      ) {
        attrs {
          mergedBelow()
          mergedRight()
        }
      }
    }

    div("col-lg-4") {
      floatingSelectNullable(
        selectedValue = unusableArea.rightTriangleType,
        onChange = {},
        availableOptionsWithoutNull = RightTriangleType.entries,
        formatter = { it?.format() ?: "Rechteck" },
        idProvider = { it.name },
        fieldName = "unusableAreaRightTriangleType$index",
        title = "Flächentyp",
        editableStatus = EditableStatus.ReadOnly,
      ) {
        attrs {
          mergedBelow()
          mergedLeft()
          mergedRight()
        }
      }
    }

    div("col-lg-2") {
      div("input-group") {
        floatingDoubleInputField(
          value = (roofSize.height / lengthFactorForEditing) - (yToSave.value + heightToSave.value),
          onChange = {
            val newValue = it.toDouble()
            val lowerBound = usableRoofArea.top / lengthFactorForEditing
            val upperBound = usableRoofArea.bottom / lengthFactorForEditing - heightToSave.value
            val adjustedValue = (roofSize.height / lengthFactorForEditing - (newValue + heightToSave.value)).coerceIn(lowerBound, upperBound.coerceAtLeast(lowerBound))
            yToSave.setter(adjustedValue)
          },
          fieldName = "unusableAreaMinusY$index",
          numberOfDecimals = numberOfDecimals,
          title = "Abstand oben",
          editableStatus = editableStatus,
        ) {
          attrs {
            mergedBelow()
            mergedLeft()
          }
        }
        span("input-group-text") {
          +lengthUnit
        }
      }
    }

    div("col-lg-3") {
      div("input-group") {
        floatingDoubleInputField(
          valueAndSetter = widthToSave,
          fieldName = "unusableAreaWidth$index",
          title = "Breite",
          numberOfDecimals = numberOfDecimals,
          numberConstraint = CoerceIn(0, (usableRoofArea.size.width / lengthFactorForEditing).toIntCeil()),
          editableStatus = editableStatus,
        ) {
          attrs {
            mergedAbove()
            mergedRight()
          }
        }
        span("input-group-text") {
          +lengthUnit
        }
      }
    }
    div("col-lg-3") {
      div("input-group") {
        floatingDoubleInputField(
          valueAndSetter = heightToSave,
          fieldName = "unusableAreaHeight$index",
          title = "Höhe",
          numberOfDecimals = numberOfDecimals,
          numberConstraint = CoerceIn(0, (usableRoofArea.size.height / lengthFactorForEditing).toIntCeil()),
          editableStatus = editableStatus,
        ) {
          attrs {
            mergedAbove()
            mergedLeft()
            mergedRight()
          }
        }
        span("input-group-text") {
          +lengthUnit
        }
      }
    }

    div("col-lg-2") {
      div("input-group") {
        floatingDoubleInputField(
          value = xToSave.value,
          onChange = {
            val newValue = it.toDouble()
            val lowerBound = usableRoofArea.left / lengthFactorForEditing
            val upperBound = usableRoofArea.right / lengthFactorForEditing - widthToSave.value
            val adjustedValue = newValue.coerceIn(lowerBound, upperBound.coerceAtLeast(lowerBound))
            xToSave.setter(adjustedValue)
          },
          fieldName = "unusableAreaX$index",
          title = "Abstand links",
          numberOfDecimals = numberOfDecimals,
          editableStatus = editableStatus,
        ) {
          attrs {
            mergedAbove()
            mergedLeft()
            mergedRight()
          }
        }
        span("input-group-text") {
          +lengthUnit
        }
      }
    }
    div("col-lg-2") {
      div("input-group") {
        floatingDoubleInputField(
          value = (roofSize.width / lengthFactorForEditing) - (xToSave.value + widthToSave.value),
          onChange = {
            val newValue = it.toDouble()
            val lowerBound = usableRoofArea.left / lengthFactorForEditing
            val upperBound = usableRoofArea.right / lengthFactorForEditing - widthToSave.value
            val adjustedValue = (roofSize.width / lengthFactorForEditing - (newValue + widthToSave.value)).coerceIn(lowerBound, upperBound.coerceAtLeast(lowerBound))
            xToSave.setter(adjustedValue)
          },
          fieldName = "unusableAreaMinusX$index",
          title = "Abstand rechts",
          numberOfDecimals = numberOfDecimals,
          editableStatus = editableStatus,
        ) {
          attrs {
            mergedAbove()
            mergedLeft()
            mergedRight()
          }
        }
        span("input-group-text") {
          +lengthUnit
        }
      }
    }

    div("col-lg-2") {
      div("input-group") {
        floatingDoubleInputField(
          value = yToSave.value,
          onChange = {
            val newValue = it.toDouble()
            val lowerBound = usableRoofArea.top / lengthFactorForEditing
            val upperBound = usableRoofArea.bottom / lengthFactorForEditing - heightToSave.value
            val adjustedValue = newValue.coerceIn(lowerBound, upperBound.coerceAtLeast(lowerBound))
            yToSave.setter(adjustedValue)
          },
          fieldName = "unusableAreaY$index",
          title = "Abstand unten",
          numberOfDecimals = numberOfDecimals,
          editableStatus = editableStatus,
        ) {
          attrs {
            mergedAbove()
            mergedLeft()
          }
        }
        span("input-group-text") {
          +lengthUnit
        }
      }
    }

  }

}


external interface EditUnusableAreaProps : Props {
  var unusableAreasInformationToSave: StateInstance<UnusableAreasInformation>
  var unusableArea: UnusableAreaInformation
  var index: Int
  var roofSize: @mm Size
  var editableStatus: EditableStatus
}
